import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Spin,
  Row,
  Col,
  Card,
  DatePicker,
} from 'antd';
import Select from 'components/Select';
import NumericInput from 'components/NumericInput';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import { deleteCuentaDeLaEntidad, getCuentasDeLaEntidad } from 'api/configuracion/cuentasDeLaEntidad';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';

export const model = 'cuentadelaentidad';

const Cuentas = ({ disabled }) => {
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getCuentasDeLaEntidad();
    setData(_data);
    setLoading(false);
  };

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      const _data = await getCuentasDeLaEntidad();
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteCuentaDeLaEntidad(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const columns = [
    {
      titleText: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      width: 300,
    },
    {
      titleText: 'Número de Usuarios Totales',
      dataIndex: 'numero_de_usuarios_totales',
      key: 'numero_de_usuarios_totales',
      width: 250,
    },
    {
      titleText: 'Número de Usuarios Disponibles',
      dataIndex: 'numero_de_usuarios_disponibles',
      key: 'numero_de_usuarios_disponibles',
      width: 270,
    },
    {
      titleText: 'Cantidad de Movimientos',
      dataIndex: 'cantidad_movimientos',
      key: 'cantidad_movimientos',
      width: 250,
    },
    {
      titleText: 'Último Pago',
      dataIndex: 'ultimo_pago',
      key: 'ultimo_pago',
      width: 150,
      render: (e) => e?.format(DATE_FRONTEND_FORMAT),
    },
    {
      titleText: 'Vencimiento',
      dataIndex: 'vencimiento',
      key: 'vencimiento',
      width: 150,
      render: (e) => e?.format(DATE_FRONTEND_FORMAT),
    },
    {
      titleText: 'Timbres Usados',
      dataIndex: 'timbres_usados',
      key: 'timbres_usados',
      width: 200,
    },
    {
      titleText: 'Timbres Restantes',
      dataIndex: 'timbres_restantes',
      key: 'timbres_restantes',
      width: 200,
    },
  ];

  return (
    <Spin tip="Cargando..." spinning={loading}>
      {!visible ? (
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          rowKey="id"
          mobileColIndex={0}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickExpand: () => setVisible(true),
          }}
          disabled={disabled}
          allowExpand
          hasAllViewPermission
        />
      ) : (
        <Card
          className="form-container"
          title={(
            <FormSubmitControls
              label="Vista Completa de Cuenta"
              onCancel={onCancel}
            />
          )}
          bordered={false}
        >
          <Form
            layout="vertical"
            form={form}
            scrollToFirstError
            className="without-feedback"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="UUID"
                  name="uuid"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <NumericInput
                  name="numero_de_usuarios_totales"
                  label="Número de Usuarios Totales"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <NumericInput
                  name="numero_de_usarios_disponibles"
                  label="Número de Usuarios Disponibles"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <NumericInput
                  name="cantidad_movimientos"
                  label="Cantidad de Movimientos"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="ultimo_pago"
                  label="Último Pago"
                >
                  <DatePicker
                    placeholder=""
                    format="DD/MM/YYYY"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="vencimiento"
                  label="Vencimiento"
                >
                  <DatePicker
                    placeholder=""
                    format="DD/MM/YYYY"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="Url Timbrado"
                  name="url_timbrado"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="Url Sac"
                  name="url_sac"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <NumericInput
                  name="timbres_usados"
                  label="Timbres Usados"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <NumericInput
                  name="timbres_restantes"
                  label="Timbres Restantes"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="Estado"
                  name="estados_globales"
                >
                  <Select dataSource={estadosGlobales} disabled />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Card>
      )}
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="La cuenta"
        loading={loading}
      />
    </Spin>
  );
};

Cuentas.propTypes = {
  disabled: PropTypes.bool,
};

Cuentas.defaultProps = {
  disabled: false,
};

export default Cuentas;
